import { LogCategory } from '@soteria/logger/browser';

import { logger } from './index';

/**
 * Error severity levels
 */
export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

/**
 * Error categories for classification
 */
export enum ErrorType {
  NETWORK = 'network',
  VALIDATION = 'validation',
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  BUSINESS_LOGIC = 'business_logic',
  SYSTEM = 'system',
  UNKNOWN = 'unknown',
}

/**
 * Interface for error context
 */
interface ErrorContext {
  userId?: string;
  sessionId?: string;
  url?: string;
  userAgent?: string;
  timestamp?: string;
  additionalData?: Record<string, unknown>;
}

/**
 * Interface for error recovery tracking
 */
interface ErrorRecovery {
  recoveryAction: string;
  success: boolean;
  userImpact: 'none' | 'minor' | 'major' | 'critical';
  details?: Record<string, unknown>;
}

/**
 * Categorize error based on error message and type
 */
function categorizeError(error: Error): { type: ErrorType; severity: ErrorSeverity } {
  const message = error.message.toLowerCase();
  const name = error.name.toLowerCase();

  // Check for network errors
  if (isNetworkError(message, name)) {
    return { type: ErrorType.NETWORK, severity: ErrorSeverity.MEDIUM };
  }

  // Check for auth errors
  if (isAuthError(message)) {
    return { type: ErrorType.AUTHENTICATION, severity: ErrorSeverity.HIGH };
  }

  // Check for authorization errors
  if (isAuthzError(message)) {
    return { type: ErrorType.AUTHORIZATION, severity: ErrorSeverity.HIGH };
  }

  // Check for validation errors
  if (isValidationError(message)) {
    return { type: ErrorType.VALIDATION, severity: ErrorSeverity.LOW };
  }

  // Check for system errors
  if (isSystemError(name)) {
    return { type: ErrorType.SYSTEM, severity: ErrorSeverity.CRITICAL };
  }

  return { type: ErrorType.UNKNOWN, severity: ErrorSeverity.MEDIUM };
}

function isNetworkError(message: string, name: string): boolean {
  return message.includes('network') || message.includes('fetch') || name.includes('networkerror');
}

function isAuthError(message: string): boolean {
  return message.includes('unauthorized') || message.includes('authentication') || message.includes('401');
}

function isAuthzError(message: string): boolean {
  return message.includes('forbidden') || message.includes('authorization') || message.includes('403');
}

function isValidationError(message: string): boolean {
  return message.includes('validation') || message.includes('invalid') || message.includes('required');
}

function isSystemError(name: string): boolean {
  return name.includes('referenceerror') || name.includes('typeerror') || name.includes('syntaxerror');
}

/**
 * Track a categorized error with context
 */
export function trackError(error: Error, context?: ErrorContext) {
  const { type, severity } = categorizeError(error);
  const errorContext: ErrorContext = {
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    ...context,
  };

  logger.error(`${severity.toUpperCase()} ${type} error: ${error.message}`, {
    category: LogCategory.ERROR,
    action: 'error_tracked',
    error: {
      name: error.name,
      message: error.message,
      stack: error.stack,
    },
    errorType: type,
    errorSeverity: severity,
    ...errorContext,
  });
}

/**
 * Track error recovery attempts
 */
export function trackErrorRecovery(error: Error, recovery: ErrorRecovery, context?: ErrorContext) {
  const { type, severity } = categorizeError(error);

  logger.info(`Error recovery ${recovery.success ? 'successful' : 'failed'}: ${recovery.recoveryAction}`, {
    category: LogCategory.ERROR,
    action: 'error_recovery',
    error: {
      name: error.name,
      message: error.message,
      type,
      severity,
    },
    recovery: {
      action: recovery.recoveryAction,
      success: recovery.success,
      userImpact: recovery.userImpact,
      ...recovery.details,
    },
    timestamp: new Date().toISOString(),
    ...context,
  });
}

/**
 * Track user impact assessment for errors
 */
export function trackUserImpact(error: Error, impact: 'none' | 'minor' | 'major' | 'critical', details?: Record<string, unknown>) {
  const { type, severity } = categorizeError(error);

  logger.warn(`User impact assessment: ${impact}`, {
    category: LogCategory.ERROR,
    action: 'user_impact_assessment',
    error: {
      name: error.name,
      message: error.message,
      type,
      severity,
    },
    userImpact: impact,
    timestamp: new Date().toISOString(),
    ...details,
  });
}

/**
 * Create a wrapper function for error-prone operations
 */
export function withErrorTracking<T extends (...args: unknown[]) => unknown>(
  fn: T,
  context?: { operationName: string; additionalContext?: Record<string, unknown> }
): T {
  return ((...args: Parameters<T>) => {
    try {
      const result = fn(...args);

      // Handle promises
      if (result && typeof result === 'object' && 'catch' in result && typeof result.catch === 'function') {
        return (result as Promise<unknown>).catch((error: Error) => {
          trackError(error, {
            additionalData: {
              operationName: context?.operationName || fn.name,
              arguments: args,
              ...context?.additionalContext,
            },
          });
          throw error;
        });
      }

      return result;
    } catch (error) {
      if (error instanceof Error) {
        trackError(error, {
          additionalData: {
            operationName: context?.operationName || fn.name,
            arguments: args,
            ...context?.additionalContext,
          },
        });
      }

      throw error;
    }
  }) as T;
}
